import React, { useEffect, useState } from 'react';
import { X, Share, PlusSquare, Smartphone, Download, MoreVertical } from 'lucide-react';

const DISMISS_KEY = 'pw_a2hs_dismissed_v1';

// Prompts users to add Precision Wealth to their home screen.
// - Android/Chrome that fire beforeinstallprompt: one-tap install button.
// - iOS Safari: manual Share -> Add to Home Screen guidance.
// - Other browsers: generic menu guidance.
export default function AddToHomeScreen() {
  const [deferred, setDeferred] = useState(null);
  const [visible, setVisible] = useState(false);
  const [platform, setPlatform] = useState('generic'); // 'install' | 'ios' | 'generic'

  useEffect(() => {
    if (localStorage.getItem(DISMISS_KEY)) return;

    // "Add to home screen" is a mobile concept; skip on desktop viewports so the
    // fixed bottom prompt never overlaps or blocks page content.
    const isMobile = window.matchMedia('(max-width: 768px)').matches;
    if (!isMobile) return;

    const standalone =
      window.matchMedia('(display-mode: standalone)').matches || window.navigator.standalone === true;
    if (standalone) return;

    const ua = window.navigator.userAgent || '';
    const iOS = /iphone|ipad|ipod/i.test(ua) && !window.MSStream;

    const onBeforeInstall = (e) => {
      e.preventDefault();
      setDeferred(e);
      setPlatform('install');
      setVisible(true);
    };
    window.addEventListener('beforeinstallprompt', onBeforeInstall);

    // Fallback: if no native install event fires shortly, show manual guidance.
    const t = setTimeout(() => {
      setVisible((v) => {
        if (v) return v; // already shown via native event
        setPlatform(iOS ? 'ios' : 'generic');
        return true;
      });
    }, 3000);

    return () => {
      window.removeEventListener('beforeinstallprompt', onBeforeInstall);
      clearTimeout(t);
    };
  }, []);

  const dismiss = () => {
    localStorage.setItem(DISMISS_KEY, '1');
    setVisible(false);
  };

  const install = async () => {
    if (!deferred) return;
    deferred.prompt();
    try { await deferred.userChoice; } catch (e) { /* ignore */ }
    setDeferred(null);
    dismiss();
  };

  if (!visible) return null;

  return (
    <div
      className="fixed bottom-4 left-1/2 -translate-x-1/2 z-[200] w-[calc(100%-2rem)] max-w-md"
      data-testid="a2hs-prompt"
    >
      <div className="pw-glass rounded-2xl p-4 shadow-2xl border border-[#C0975C]/40">
        <div className="flex items-start gap-3">
          <div className="w-11 h-11 rounded-xl bg-[#C0975C] flex items-center justify-center flex-shrink-0">
            <Smartphone className="w-6 h-6 text-[#0A1F44]" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-white font-bold text-sm" style={{ fontFamily: "'Outfit', sans-serif" }}>
              Add Precision Wealth to your home screen
            </p>

            {platform === 'install' && (
              <>
                <p className="text-[#c6d0e0] text-xs mt-1 leading-relaxed">
                  Install the app for a faster, full-screen experience with one tap.
                </p>
                <button
                  onClick={install}
                  data-testid="a2hs-install-btn"
                  className="neo-button-primary inline-flex items-center gap-2 px-4 py-2 mt-3 text-sm"
                >
                  <Download className="w-4 h-4" /> Add to Home Screen
                </button>
              </>
            )}

            {platform === 'ios' && (
              <p className="text-[#c6d0e0] text-xs mt-1 leading-relaxed flex items-center flex-wrap gap-1">
                Tap the Share icon <Share className="w-3.5 h-3.5 inline text-[#C0975C]" /> then
                <span className="font-semibold text-white">Add to Home Screen</span>
                <PlusSquare className="w-3.5 h-3.5 inline text-[#C0975C]" /> for the full experience.
              </p>
            )}

            {platform === 'generic' && (
              <p className="text-[#c6d0e0] text-xs mt-1 leading-relaxed flex items-center flex-wrap gap-1">
                Open your browser menu <MoreVertical className="w-3.5 h-3.5 inline text-[#C0975C]" /> and tap
                <span className="font-semibold text-white">Add to Home screen</span> for the full experience.
              </p>
            )}
          </div>
          <button onClick={dismiss} data-testid="a2hs-dismiss" className="text-[#94A9C9] hover:text-white p-1 flex-shrink-0">
            <X className="w-5 h-5" />
          </button>
        </div>
      </div>
    </div>
  );
}
